import { useId, useState, type KeyboardEvent } from 'react';
import {
  LINE_ITEM_MENU_STYLE,
  LINE_ITEM_MENU_TRIGGER_STYLE,
  lineItemMenuItemStyle
} from '@/document-render/presentation/blocks/lineItems/lineItemsQuoteStyles';

export interface LineItemMenuAction {
  label: string;
  disabled?: boolean;
  onSelect: () => void;
}

/**
 * A line's overflow menu: a quiet "⋮" trigger and a small popover of actions. Escape and picking an
 * action close it. The trigger keeps its accessible name (`label`) — the visible glyph is decorative.
 */
export function LineItemActionsMenu({ label, actions }: { label: string; actions: readonly LineItemMenuAction[] }) {
  const [open, setOpen] = useState(false);
  const menuId = useId();

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') setOpen(false);
  };

  return (
    <span style={{ position: 'relative', display: 'inline-block' }} onKeyDown={onKeyDown} role="presentation">
      <button
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        style={LINE_ITEM_MENU_TRIGGER_STYLE}
        onClick={() => setOpen((current) => !current)}
      >
        <span aria-hidden="true">{'⋮'}</span>
      </button>
      {open ? (
        <div id={menuId} role="menu" aria-label={label} style={LINE_ITEM_MENU_STYLE}>
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              role="menuitem"
              disabled={action.disabled}
              style={lineItemMenuItemStyle(Boolean(action.disabled))}
              onClick={() => {
                setOpen(false);
                action.onSelect();
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </span>
  );
}
